/**
 * @file 紙面の上で範囲を囲む層である (選んで聞く 段階4)。
 *
 * @what: 画像の実際に表示されている範囲 (object-fit: contain の余白を除いた矩形) に重なり、
 *   押して引くと四角を出す。指を離すと、辺の長さを1とした割合で呼び出し側へ渡す。小さすぎる
 *   囲み (押し損ね) は渡さない。
 * @why: 紙面の一部について聞くとき、どこを聞いているかを参照そのものに持たせるためである
 *   (`page:<資料>/<連番>@x,y,w,h`)。切り取りはサーバが行う。
 * @failure: 画像がまだ無いときは何も出さない。なぞる操作しか持たないので、読み上げの対象からは
 *   外す (キーボードだけのときは、ヘッダーの「このページを選ぶ」でページ全体を選ぶ)。
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

/** これより小さい囲みは、押し損ねとみなして捨てる (割合)。 */
const MIN_RATIO = 0.03;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 画像の要素の箱の中で、実際に表示されている範囲を出す (object-fit: contain)。 */
function drawnRect(img: HTMLImageElement, stage: HTMLElement): Box | null {
  const box = img.getBoundingClientRect();
  const base = stage.getBoundingClientRect();
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (nw <= 0 || nh <= 0 || box.width <= 0 || box.height <= 0) return null;
  const scale = Math.min(box.width / nw, box.height / nh);
  const w = nw * scale;
  const h = nh * scale;
  return {
    left: box.left - base.left + (box.width - w) / 2,
    top: box.top - base.top + (box.height - h) / 2,
    width: w,
    height: h,
  };
}

export function PageRectLayer({
  enabled,
  onSelect,
}: {
  /** 「範囲を囲む」が入っているか。外れている間はポインタを下の画像へ通す。 */
  enabled: boolean;
  /** 囲み終えたときに、割合 (0〜1) で渡す。 */
  onSelect: (rect: { x: number; y: number; w: number; h: number }) => void;
}) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [area, setArea] = useState<Box | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  // 押し始めた点は ref にも持つ。※なぜなら押した直後に動かすと、まだ再描画されておらず、
  //   イベントの処理が古い state を見てしまうためである。
  const startRef = useRef<{ x: number; y: number; id: number } | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const measure = useCallback(() => {
    if (!host) return;
    const stage = host.parentElement;
    const img = stage?.querySelector("img");
    if (!stage || !(img instanceof HTMLImageElement) || !img.complete || img.naturalWidth === 0) {
      setArea(null);
      return;
    }
    setArea(drawnRect(img, stage));
  }, [host]);

  useEffect(() => {
    if (!host) return;
    measure();
    const stage = host.parentElement;
    const img = stage?.querySelector("img");
    const onLoad = () => measure();
    img?.addEventListener("load", onLoad);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && stage) {
      ro = new ResizeObserver(() => measure());
      ro.observe(stage);
      if (img) ro.observe(img);
    }
    window.addEventListener("resize", measure);
    return () => {
      img?.removeEventListener("load", onLoad);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [host, measure]);

  // 囲みをやめたら、途中まで引いた四角は消す。
  useEffect(() => {
    if (!enabled) {
      startRef.current = null;
      setDrag(null);
    }
  }, [enabled]);

  if (!enabled || area === null) {
    return <div ref={setHost} className="study-page-rect" data-enabled="false" />;
  }

  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1),
      y: Math.min(Math.max((e.clientY - box.top) / box.height, 0), 1),
    };
  };

  const shown =
    drag === null
      ? null
      : {
          left: `${Math.min(drag.x0, drag.x1) * 100}%`,
          top: `${Math.min(drag.y0, drag.y1) * 100}%`,
          width: `${Math.abs(drag.x1 - drag.x0) * 100}%`,
          height: `${Math.abs(drag.y1 - drag.y0) * 100}%`,
        };

  return (
    <div ref={setHost} className="study-page-rect" data-enabled="true">
      <div
        className="study-page-rect-area"
        aria-hidden="true"
        style={{ left: area.left, top: area.top, width: area.width, height: area.height }}
        onPointerDown={(e) => {
          if (startRef.current !== null) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          const p = at(e);
          startRef.current = { ...p, id: e.pointerId };
          setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
        }}
        onPointerMove={(e) => {
          const start = startRef.current;
          // 2本目の指や手の側面が触れても、1本目の囲みを乗っ取らせない。
          if (start === null || start.id !== e.pointerId) return;
          const p = at(e);
          setDrag({ x0: start.x, y0: start.y, x1: p.x, y1: p.y });
        }}
        onPointerUp={(e) => {
          const start = startRef.current;
          if (start === null || start.id !== e.pointerId) return;
          const p = at(e);
          startRef.current = null;
          setDrag(null);
          const x = Math.min(start.x, p.x);
          const y = Math.min(start.y, p.y);
          const w = Math.abs(p.x - start.x);
          const h = Math.abs(p.y - start.y);
          if (w >= MIN_RATIO && h >= MIN_RATIO) onSelectRef.current({ x, y, w, h });
        }}
        onPointerCancel={(e) => {
          if (startRef.current !== null && startRef.current.id !== e.pointerId) return;
          startRef.current = null;
          setDrag(null);
        }}
      >
        {shown ? <div className="study-page-rect-box" style={shown} /> : null}
      </div>
    </div>
  );
}
